import { combineEpics, ofType } from 'redux-observable';
import { empty, of } from 'rxjs';
import { concatMap, ignoreElements, mergeMap, tap } from 'rxjs/operators';
import { ConflictedAuthenticationResult } from '../../generated/api/models.js';
import { getSubscriptionsPrivateMethod, resendVerifyEmailLinkPrivateMethod } from '../../generated/api/uiApiMethods.js';
import { getEmployeeSubscriptionType } from '../../public/common/util/category';
import {
  TypeKeys,
  checkValidEmployeeSubscription,
  dispatchAuthAction,
  getEmployeeSubscriptionsFailed,
  getEmployeeSubscriptionsFulfilled,
  resendEmailVerificationLinkFailed,
  resendEmailVerificationLinkFulfilled,
} from '../actions/index.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { SubscriptionCategory } from '../../common/enums.js';
import type { CommonError } from '../../common/types/errors.js';
import type { SubscriptionsResponse } from '../../generated/api/models.js';
import type {
  ActionTypes,
  GetEmployeeSubscriptionsAction,
  GetEmployeeSubscriptionsFulfilledAction,
  ResendEmailVerificationLinkAction,
} from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';

// state key
export const AUTHENTICATED = 'authenticated';

const getEmployeeSubscriptionsQueryParams = (subscriptionType: SubscriptionCategory) => {
  return {
    details: true,
    subscriptionType: getEmployeeSubscriptionType(subscriptionType),
  };
};

export const getConflictingErrors = (
  errors?: CommonError[]
): { type: ConflictedAuthenticationResult.TypeEnum; conflictingAccountName?: string } | undefined => {
  const { conflictingAccountName, type } = (errors?.[0].source?.error || {}) as ConflictedAuthenticationResult;
  switch (type) {
    case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_ACCOUNT:
      return { type, conflictingAccountName };
    case ConflictedAuthenticationResult.TypeEnum.DUPLICATE_CONTACT:
    case ConflictedAuthenticationResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE:
    case ConflictedAuthenticationResult.TypeEnum.CONTACT_SUPPORT:
    case ConflictedAuthenticationResult.TypeEnum.USER_NOT_FOUND:
    case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_CONTACT:
      return { type };
    default:
      return undefined;
  }
};

// get user subscriptions
const getEmployeeSubscriptionsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS)),
    (action: GetEmployeeSubscriptionsAction) => actionToActionState(action, state$, AUTHENTICATED)
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const action = actionAndState.action as GetEmployeeSubscriptionsAction;
      return callUiApi({
        epicDependencies,
        state$,
        method: getSubscriptionsPrivateMethod(getEmployeeSubscriptionsQueryParams(action.subscriptionType)),
        successAction: (response: SubscriptionsResponse) =>
          getEmployeeSubscriptionsFulfilled(response, action.subscriptionType, action.subscriptionItemId),
        failureAction: getEmployeeSubscriptionsFailed,
      });
    })
  );

const completeGetEmployeeSubscriptionsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.ofType(TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED).pipe(
    mergeMap((action: GetEmployeeSubscriptionsFulfilledAction) => {
      if (action.subscriptionItemId !== undefined) {
        return of(checkValidEmployeeSubscription(action.subscriptionType, action.subscriptionItemId));
      }
      return empty();
    })
  );

// Order a new link to verify email
export const resendEmailVerificationLinkEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.RESEND_EMAIL_VERIFICATION_LINK)),
    (action: ResendEmailVerificationLinkAction) => actionToActionState(action, state$, AUTHENTICATED)
  ).pipe(
    concatMap(() => {
      return callUiApi({
        epicDependencies,
        state$,
        method: resendVerifyEmailLinkPrivateMethod(),
        successAction: resendEmailVerificationLinkFulfilled,
        failureAction: resendEmailVerificationLinkFailed,
      });
    })
  );

export const resetErrorsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.pipe(
    ofType(TypeKeys.RESET_ERRORS),
    tap(() => dispatchAuthAction('RESET_ERRORS')),
    ignoreElements()
  );

export const authenticatedUserEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  getEmployeeSubscriptionsEpic,
  completeGetEmployeeSubscriptionsEpic,
  resendEmailVerificationLinkEpic,
  resetErrorsEpic
);
